import { useMemo, useState } from 'react'

import Button from 'common/components/button'
import Catalog from 'common/components/catalog/Catalog'
import ConfirmationModal from 'common/components/confirmationModal/ConfirmationModal'
import Table, { Column } from 'common/components/table'
import BaseCell from 'common/components/table/cells/Base'
import { generateDeleteButtonColumn } from 'common/components/table/columns'
import { errorToast } from 'common/components/toastNotification'
import { formatDate } from 'common/helpers/date'
import {
  ChevronDown as ChevronDownIcon,
  ChevronRight as ChevronRightIcon,
} from 'common/icons'

import useGetProductCatalogQuery, {
  ProductCategory,
} from '../api/queries/useGetProductCatalogQuery'

type Props = {
  baseUrl: string
}

const ProductCategoriesTable = ({ baseUrl }: Props) => {
  const [isCollapsed, setIsCollapsed] = useState<boolean>(false)

  const { data: productCatalog } = useGetProductCatalogQuery({
    baseUrl,
  })
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState<boolean>(false)

  const columns: Column<ProductCategory>[] = useMemo(
    () => [
      {
        Header: 'Name',
        accessor: 'name',
        width: 600,
        minWidth: 200,
        Cell: ({ row }) => {
          return (
            <BaseCell className="bg-coolGray-50">
              <Button
                className="hover:text-orange-400 focus:outline-none focus-visible:outline-black group -ml-4 max-w-min"
                onClick={() => setIsCollapsed(!isCollapsed)}
                prefixIcon={
                  isCollapsed ? (
                    <ChevronRightIcon size={4} />
                  ) : (
                    <ChevronDownIcon isDefaultColor={false} size={4} />
                  )
                }
              >
                <span className="overflow-hidden truncate">
                  {row.original.name}
                </span>
              </Button>
            </BaseCell>
          )
        },
        disableSortBy: true,
      },
      {
        Header: 'Type',
        accessor: 'type',
        width: 80,
        disableSortBy: true,
        Cell: ({ row }) => {
          return <BaseCell className="uppercase">{row.original.type}</BaseCell>
        },
      },
      {
        Header: 'Created',
        accessor: 'created',
        width: 80,
        minWidth: 110,
        disableSortBy: true,
        Cell: ({ row }) => {
          return <BaseCell>{formatDate(row.original.created)}</BaseCell>
        },
      },
      {
        ...generateDeleteButtonColumn<ProductCategory>({
          onClick: (row) => errorToast('Not implemented yet'),
          buttonLabel: 'Delete',
          hasTooltip: true,
          isVisibleOnlyOnHover: false,
          isDefaultColor: false,
        }),
        Header: 'Actions',
        width: 80,
        minWidth: 110,
        disableSortBy: true,
      },
    ],
    [isCollapsed, setIsCollapsed]
  )

  if (!productCatalog) {
    return null
  }
  return (
    <div className="sm:-ml-6 sm:-mr-6">
      <Table
        columns={columns}
        data={[productCatalog]}
        className="mt-6"
        rowClassName="bg-coolGray-50 "
      />
      {!isCollapsed && <Catalog items={productCatalog.categories} />}
      <ConfirmationModal
        title="Delete this file?"
        confirmationText="Are you sure you want to delete this file?"
        confirmButtonText="Delete file"
        open={isConfirmDeleteOpen}
        onCancel={() => setIsConfirmDeleteOpen(false)}
        onConfirm={() => {
          setIsConfirmDeleteOpen(false)
          errorToast('Not implemented yet')
        }}
      />
    </div>
  )
}

export default ProductCategoriesTable
